import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import {
  useSubscriptionPlansQuery,
  useCreateSubscriptionPlanMutation,
  useSaveSubscriptionPlanMutation,
  useDeleteSubscriptionPlanMutation
} from "@queries/website";
import "@styles/opencart-admin.css";

const defaultForm = {
  name: "",
  name_ar: "",
  price: 0,
  bags: 0,
  bags_label: "",
  bags_label_ar: "",
  save_label: "",
  save_label_ar: "",
  order_type: "service",
  frequency: "weekly",
  tone: "primary",
  status: true,
  sort_order: 0
};

export default function SubscriptionPlansPage() {
  const [filters, setFilters] = React.useState({ name: "", status: "" });
  const [page, setPage] = React.useState(1);
  const pageSize = 20;

  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(defaultForm);
  const [validationErrors, setValidationErrors] = React.useState({});
  const formCardRef = React.useRef(null);

  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, isError, error } = useSubscriptionPlansQuery(
    {
      name: filters.name || undefined,
      status: filters.status === "" ? undefined : filters.status
    },
    page,
    pageSize
  );

  const createMutation = useCreateSubscriptionPlanMutation();
  const saveMutation = useSaveSubscriptionPlanMutation();
  const deleteMutation = useDeleteSubscriptionPlanMutation();

  const rows = data?.data || [];
  const total = data?.total ?? data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  function resetForm() {
    setEditingId(null);
    setForm(defaultForm);
    setValidationErrors({});
  }

  function openEdit(row) {
    setValidationErrors({});
    setEditingId(row.plan_id);
    setForm({
      name: row.name || "",
      name_ar: row.name_ar || "",
      price: row.price ?? 0,
      bags: row.bags ?? 0,
      bags_label: row.bags_label || "",
      bags_label_ar: row.bags_label_ar || "",
      save_label: row.save_label || "",
      save_label_ar: row.save_label_ar || "",
      order_type: row.order_type || "service",
      frequency: row.frequency || "weekly",
      tone: row.tone || "primary",
      status: !!row.status,
      sort_order: row.sort_order ?? 0
    });
    window.requestAnimationFrame(() => {
      formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  async function handleSave(e) {
    e.preventDefault();
    setValidationErrors({});

    const payload = {
      name: form.name,
      name_ar: form.name_ar || null,
      price: Number(form.price) || 0,
      bags: Number(form.bags) || 0,
      bags_label: form.bags_label || null,
      bags_label_ar: form.bags_label_ar || null,
      save_label: form.save_label || null,
      save_label_ar: form.save_label_ar || null,
      order_type: form.order_type,
      frequency: form.frequency,
      tone: form.tone,
      status: form.status ? 1 : 0,
      sort_order: Number(form.sort_order) || 0
    };

    try {
      if (editingId) {
        await saveMutation.mutateAsync({ id: editingId, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["website", "subscription-plans"] });
    } catch (err) {
      if (err?.response?.status === 422 && err?.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      } else {
        alert(err?.response?.data?.message || "Failed to save plan");
      }
    }
  }

  async function handleDelete(row) {
    if (!window.confirm(`Delete plan "${row.name}"?`)) return;
    try {
      await deleteMutation.mutateAsync(row.plan_id);
      queryClient.invalidateQueries({ queryKey: ["website", "subscription-plans"] });
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete plan");
    }
  }

  const fieldError = (key) =>
    validationErrors[key] && (
      <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
        {validationErrors[key][0]}
      </div>
    );

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Subscription Plans</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Website</span>
          <span className="sep">›</span>
          <span>Subscription Plans</span>
        </nav>
      </div>

      <div className="oc-card" ref={formCardRef} style={{ scrollMarginTop: 24 }}>
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>{editingId ? `Edit Plan #${editingId}` : "Add Plan"}</strong>
          </div>
          <div>
            <button className="oc-btn ghost" onClick={resetForm}>
              New
            </button>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div className="form-row">
            <div className="form-group">
              <label>Name (English)</label>
              <input
                className="oc-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
              {fieldError("name")}
            </div>
            <div className="form-group">
              <label>Name (Arabic)</label>
              <input
                className="oc-input"
                dir="rtl"
                placeholder="الاسم بالعربية"
                value={form.name_ar}
                onChange={(e) => setForm((f) => ({ ...f, name_ar: e.target.value }))}
              />
              {fieldError("name_ar")}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Price (JOD)</label>
              <input
                type="number"
                step="0.01"
                className="oc-input"
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                required
              />
              {fieldError("price")}
            </div>
            <div className="form-group">
              <label>Bags</label>
              <input
                type="number"
                className="oc-input"
                value={form.bags}
                onChange={(e) => setForm((f) => ({ ...f, bags: e.target.value }))}
              />
              {fieldError("bags")}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Bags Label (English)</label>
              <input
                className="oc-input"
                placeholder="e.g. 4 Washy Bags"
                value={form.bags_label}
                onChange={(e) => setForm((f) => ({ ...f, bags_label: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Bags Label (Arabic)</label>
              <input
                className="oc-input"
                dir="rtl"
                placeholder="مثال: 4 أكياس واشي"
                value={form.bags_label_ar}
                onChange={(e) => setForm((f) => ({ ...f, bags_label_ar: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Save Label (English)</label>
              <input
                className="oc-input"
                placeholder="e.g. Save 15% compared to one-time plan."
                value={form.save_label}
                onChange={(e) => setForm((f) => ({ ...f, save_label: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Save Label (Arabic)</label>
              <input
                className="oc-input"
                dir="rtl"
                placeholder="مثال: وفّر 15% مقارنة بالطلب لمرة واحدة."
                value={form.save_label_ar}
                onChange={(e) => setForm((f) => ({ ...f, save_label_ar: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Order Type</label>
              <input
                className="oc-input"
                value={form.order_type}
                onChange={(e) => setForm((f) => ({ ...f, order_type: e.target.value }))}
                required
              />
              {fieldError("order_type")}
            </div>
            <div className="form-group" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Frequency</label>
              <select
                className="oc-input"
                value={form.frequency}
                onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="form-group">
              <label>Tone (card style on website)</label>
              <select
                className="oc-input"
                value={form.tone}
                onChange={(e) => setForm((f) => ({ ...f, tone: e.target.value }))}
              >
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
                <label>
                  <input
                    type="radio"
                    name="status"
                    checked={form.status === true}
                    onChange={() => setForm((f) => ({ ...f, status: true }))}
                  />{" "}
                  Enabled
                </label>
                <label>
                  <input
                    type="radio"
                    name="status"
                    checked={form.status === false}
                    onChange={() => setForm((f) => ({ ...f, status: false }))}
                  />{" "}
                  Disabled
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Sort Order</label>
              <input
                type="number"
                className="oc-input"
                value={form.sort_order}
                onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="oc-btn ghost" onClick={resetForm}>
              Cancel
            </button>
            <button
              type="submit"
              className="oc-btn primary"
              disabled={createMutation.isPending || saveMutation.isPending}
            >
              Save
            </button>
          </div>
        </form>
      </div>

      <div className="oc-card" style={{ marginTop: 16 }}>
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>Plan List</strong>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 260px", gap: 16 }}>
          <div>
            {isError && (
              <div style={{ color: "#c00", marginBottom: 8 }}>
                Error: {error?.message || "Unknown error"}
              </div>
            )}

            <table className="oc-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>ID</th>
                  <th>Name</th>
                  <th style={{ width: 100 }}>Price</th>
                  <th style={{ width: 70 }}>Bags</th>
                  <th style={{ width: 100 }}>Frequency</th>
                  <th style={{ width: 110 }}>Subscribers</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 150 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: 24 }}>
                      No plans
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.plan_id}>
                    <td>{row.plan_id}</td>
                    <td>
                      {row.name}
                      {row.name_ar ? (
                        <div dir="rtl" style={{ fontSize: 12, color: "#2563eb", textAlign: "left" }}>
                          {row.name_ar}
                        </div>
                      ) : null}
                      {row.bags_label ? (
                        <div style={{ fontSize: 12, color: "#777" }}>{row.bags_label}</div>
                      ) : null}
                    </td>
                    <td>{Number(row.price).toFixed(2)}</td>
                    <td>{row.bags}</td>
                    <td>{row.frequency}</td>
                    <td>{row.subscriptions_count ?? 0}</td>
                    <td>{row.status ? "Enabled" : "Disabled"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="oc-btn blue" onClick={() => openEdit(row)}>
                          edit
                        </button>
                        <button className="oc-btn danger" onClick={() => handleDelete(row)}>
                          delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="oc-card" style={{ margin: 0 }}>
            <div className="title">Filter</div>
            <div className="form-group">
              <label>Name</label>
              <input
                className="oc-input"
                value={filters.name}
                onChange={(e) => {
                  setFilters((p) => ({ ...p, name: e.target.value }));
                  setPage(1);
                }}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                className="oc-input"
                value={filters.status}
                onChange={(e) => {
                  setFilters((p) => ({ ...p, status: e.target.value }));
                  setPage(1);
                }}
              >
                <option value="">All</option>
                <option value="1">Enabled</option>
                <option value="0">Disabled</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div
        className="oc-card"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong> ({total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="oc-btn ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </button>
          <button className="oc-btn ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
}
